/**
 * Complex scalar arithmetic over ℂ.
 *
 * A `Complex` is represented as a `[re, im]` pair, generic over the underlying
 * real scalar so that callers may specialize the carrier (e.g. `Complex<number>`
 * for floating-point work). The default carrier is `number`, which reproduces
 * the original GL(2,ℂ) tangent-embedding surface exactly.
 *
 * This module owns the field operations of ℂ. The matrix module
 * (`complex-matrix.ts`) builds Mₙ(ℂ) on top of these primitives, and the
 * GL(2,ℂ) tangent embedding (`research/gl2c-tangent-embedding.ts`) imports
 * both rather than owning either.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

/**
 * A complex number as `[re, im]`, generic over the underlying real scalar.
 *
 * `Complex` (the default) is `Complex<number>` — the floating-point carrier the
 * matrix operations in this package compute over. The type parameter lets a
 * caller name a different carrier (e.g. a bignum) without rewriting the shape.
 */
export type Complex<T = number> = readonly [re: T, im: T];

/** Additive identity 0 + 0i. */
export const cZero: Complex = [0, 0];

/** Multiplicative identity 1 + 0i. */
export const cOne: Complex = [1, 0];

/** The imaginary unit i = 0 + 1i. */
export const cI: Complex = [0, 1];

/** Lift a real number r into ℂ as r + 0i. */
export function cFromReal(r: number): Complex {
  return [r, 0];
}

/** Complex addition (a + b). */
export function cAdd(a: Complex, b: Complex): Complex {
  return [a[0] + b[0], a[1] + b[1]];
}

/** Complex subtraction (a − b). */
export function cSub(a: Complex, b: Complex): Complex {
  return [a[0] - b[0], a[1] - b[1]];
}

/** Complex multiplication (a · b). */
export function cMul(a: Complex, b: Complex): Complex {
  return [a[0] * b[0] - a[1] * b[1], a[0] * b[1] + a[1] * b[0]];
}

/** Scale a complex number by a real scalar. */
export function cScale(s: number, z: Complex): Complex {
  return [s * z[0], s * z[1]];
}

/** Additive inverse −z. */
export function cNeg(z: Complex): Complex {
  return [-z[0], -z[1]];
}

/** Complex conjugate z̄ = a − bi for z = a + bi. */
export function cConj(z: Complex): Complex {
  return [z[0], -z[1]];
}

/** Squared modulus |z|² = a² + b². */
export function cModSq(z: Complex): number {
  return z[0] * z[0] + z[1] * z[1];
}

/** Complex modulus |z|. */
export function cMod(z: Complex): number {
  return Math.hypot(z[0], z[1]);
}

/**
 * Complex division (a / b). Throws when |b| = 0, since ℂ has no inverse for 0.
 */
export function cDiv(a: Complex, b: Complex): Complex {
  const denom = cModSq(b);
  if (denom === 0) {
    throw new Error("cDiv: division by zero is undefined in ℂ");
  }
  return [(a[0] * b[0] + a[1] * b[1]) / denom, (a[1] * b[0] - a[0] * b[1]) / denom];
}

/** Complex exponential e^z = e^a (cos b, sin b) for z = a + bi. */
export function cExp(z: Complex): Complex {
  const ea = Math.exp(z[0]);
  return [ea * Math.cos(z[1]), ea * Math.sin(z[1])];
}

/**
 * Principal complex square root √z. For z = a + bi the result w satisfies
 * w² = z with Re(w) ≥ 0; the sign of Im(w) follows the sign of b.
 */
export function cSqrt(z: Complex): Complex {
  const r = cMod(z);
  const re = Math.sqrt(Math.max(0, (r + z[0]) / 2));
  const imMag = Math.sqrt(Math.max(0, (r - z[0]) / 2));
  return [re, z[1] < 0 ? -imMag : imMag];
}

/** Whether two complex numbers agree to within an absolute tolerance. */
export function cEqual(a: Complex, b: Complex, tol = 1e-10): boolean {
  return Math.abs(a[0] - b[0]) < tol && Math.abs(a[1] - b[1]) < tol;
}
